/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
    LogOut,
    Download,
    Search,
    FileText,
    Loader2,
    Receipt,
    GraduationCap,
    HeartPulse,
    Megaphone,
    FileSignature,
    File,
    ShieldCheck,
    User,
    Upload,
    KeyRound,
    Trash2,
    BookOpen,
    Wallet,
    AlertCircle,
    Inbox,
    IdCard,
    CalendarDays,
    Clock4,
    Gavel,
    Eye,
    Gift,
    Menu,
} from "lucide-react";
import {
    CATEGORY_LABELS,
    CATEGORY_ORDER,
    downloadDocument,
    formatBytes,
    formatDateTime,
} from "../lib/documents";
import { toast } from "sonner";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortableHead } from "../components/SortableHead";
import { ConsentInformativaDialog, AcknowledgmentDialog, BulkAcknowledgmentDialog } from "../components/DeliveryConsent";
import { EmployeeSummaryCard } from "../components/EmployeeSummaryCard";
import { MyWeekCard } from "../components/MyWeekCard";
import { DocumentPreviewDialog } from "../components/DocumentPreviewDialog";
import { EmployeeGuideDialog, hasSeenGuide } from "../components/EmployeeGuideDialog";
import { BadgeHelpButton } from "../components/BadgeHelpButton";
import { MyUploadsDialog } from "../components/MyUploadsDialog";
import { EditContactDataForm } from "../components/EditContactDataForm";
import { DownloadStatusBadge } from "../components/DownloadStatusBadge";
import { EmployeeAdvancesCard } from "../components/EmployeeAdvancesCard";
import OnboardingTour from "../components/OnboardingTour";

const payslipPeriodLabel = (period) => {
    const m = /^(\d{4})-(\d{2})$/.exec(period || "");
    if (!m) return null;
    const n = parseInt(m[2], 10);
    const MESI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
    if (n >= 1 && n <= 12) return `${MESI[n - 1]} ${m[1]}`;
    if (n === 13) return `tredicesima ${m[1]}`;
    if (n === 14) return `quattordicesima ${m[1]}`;
    return null;
};

const CATEGORY_ICONS = {
    busta_paga: Receipt,
    cu_730: FileSignature,
    contratto: FileText,
    certificato: GraduationCap,
    comunicazione: Megaphone,
    visita_medica: HeartPulse,
    documento_identita: IdCard,
    atti: Gavel,
    altro: File,
};

export default function EmployeePortal() {
    const { user, logout } = useAuth();
    const company = useCompany();
    const navigate = useNavigate();
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloadingId, setDownloadingId] = useState(null);
    const [search, setSearch] = useState("");
    const [filterCategory, setFilterCategory] = useState(""); // "" = no category selected (collapsed). Auto-expanded to a category with new docs on first load.
    const [autoExpandDone, setAutoExpandDone] = useState(false);
    const [ackDoc, setAckDoc] = useState(null); // document pending acknowledgment
    const [bulkAckOpen, setBulkAckOpen] = useState(false); // bulk "Firma tutti" dialog
    const [profileOpen, setProfileOpen] = useState(false);
    const [profileTab, setProfileTab] = useState("data");
    const [myUploadsOpen, setMyUploadsOpen] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadPrefill, setUploadPrefill] = useState("");
    const [uploadRequestId, setUploadRequestId] = useState(null);
    const [docRequests, setDocRequests] = useState([]);

    useEffect(() => {
        if (!uploadOpen) {
            api.get("/doc-requests/mine").then((r) => setDocRequests(r.data || [])).catch(() => {});
        }
    }, [uploadOpen]);
    const [summaryRefreshKey, setSummaryRefreshKey] = useState(0);
    const [meEmployee, setMeEmployee] = useState(null);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [pendingPreviewAckDoc, setPendingPreviewAckDoc] = useState(null);
    const [guideOpen, setGuideOpen] = useState(false);
    const [guideFirstTime, setGuideFirstTime] = useState(false);
    const [guideFocusSection, setGuideFocusSection] = useState(null);  // section id to scroll-to when guide opens
    // Advances widget state — notified by EmployeeAdvancesCard
    const [advancesInfo, setAdvancesInfo] = useState({ count: 0, hasNew: false });
    const [richiesteEnabled, setRichiesteEnabled] = useState(false);
    const [presenzeEnabled, setPresenzeEnabled] = useState(false);

    useEffect(() => {
        api.get("/presenze/richieste-config")
            .then((r) => setRichiesteEnabled(!!r.data.enabled))
            .catch(() => {});
        api.get("/presenze/config")
            .then((r) => setPresenzeEnabled(!!r.data.enabled))
            .catch(() => {});
    }, []);

    // Open the guide focused on a specific section (used by the "?" icons next to badges)
    const openGuideAt = (sectionId) => {
        setGuideFirstTime(false);
        setGuideFocusSection(sectionId);
        setGuideOpen(true);
    };

    // Auto-open guide on first login for this user
    useEffect(() => {
        if (user?.id && !hasSeenGuide(user.id)) {
            // Small delay so consent dialog has priority if needed
            const t = setTimeout(() => {
                setGuideFirstTime(true);
                setGuideOpen(true);
            }, 600);
            return () => clearTimeout(t);
        }
    }, [user?.id]);

    const fetchDocs = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/documents/mine");
            setDocs(data);
        } catch (e) {
            toast.error("Errore nel caricamento documenti");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDocs();
    }, []);

    // Silent refresh (no full-page loader) — used after download/ack/preview
    const refreshDocs = async () => {
        try {
            const { data } = await api.get("/documents/mine");
            setDocs(data);
        } catch (e) {
            console.warn("Aggiornamento silenzioso documenti fallito (stato mantenuto):", e?.message);
        }
    };

    // Fetch and keep the current employee record at page-level so the Profile
    // dialog can render the editable form without an extra round-trip.
    useEffect(() => {
        if (!user?.id) return;
        api.get(`/employees/${user.id}`)
            .then((r) => setMeEmployee(r.data))
            .catch(() => {});
    }, [user?.id, summaryRefreshKey]);

    // Ceased employee: read-only portal (documents only) until the access end date
    const isCessato = !!(meEmployee?.data_cessazione && String(meEmployee.data_cessazione).trim());
    const canOperate = !!meEmployee && !isCessato;

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const performDownload = async (doc, acknowledged = false) => {
        setDownloadingId(doc.id);
        try {
            await downloadDocument(doc.id, doc.filename, { acknowledged });
            toast.success("Documento scaricato — ricevuta archiviata");
            refreshDocs();
        } catch (e) {
            const detail = e.response?.data?.detail;
            const code = (detail && typeof detail === "object" && detail.code) || null;
            const detailStr = typeof detail === "string" ? detail : (detail?.message || "");
            const status = e.response?.status;
            if (code === "ack_required") {
                // Should not normally happen because we gate at the click, but safety net.
                setAckDoc(doc);
            } else if (code === "consent_required") {
                toast.error(
                    "Devi accettare l'informativa prima di scaricare i documenti",
                    { duration: 6000 },
                );
            } else if (status === 503 && /402|Payment Required/i.test(detailStr)) {
                toast.error(
                    "Documento temporaneamente non disponibile (servizio storage in manutenzione). " +
                    "Riprova tra qualche minuto o contatta l'ufficio del personale.",
                    { duration: 10000 },
                );
            } else if (status === 503) {
                toast.error(
                    "Servizio temporaneamente non disponibile. Riprova tra qualche secondo.",
                    { duration: 6000 },
                );
            } else if (status === 404) {
                toast.error(
                    "Documento non trovato. Potrebbe essere stato rimosso. Contatta l'ufficio del personale.",
                    { duration: 6000 },
                );
            } else {
                toast.error(detailStr || "Errore nel download del documento", { duration: 6000 });
            }
        } finally {
            setDownloadingId(null);
        }
    };

    const handleDownload = async (doc) => {
        // If the doc requires acknowledgment AND hasn't been acknowledged yet,
        // open the ack dialog. Once acknowledged, the second click downloads directly.
        if (doc.requires_acknowledgment && !doc.acknowledged) {
            setAckDoc(doc);
            return;
        }
        await performDownload(doc, false);
    };

    const handlePreview = async (doc) => {
        // Same gating as download: ask for ack only if required AND not yet acknowledged.
        if (doc.requires_acknowledgment && !doc.acknowledged) {
            setPendingPreviewAckDoc(doc);
            return;
        }
        setPreviewDoc({ doc, acknowledged: false });
    };

    const filtered = useMemo(() => {
        // When no category is selected (collapsed view) or "acconti" panel is open,
        // hide the docs table.
        if (!filterCategory || filterCategory === "acconti") return [];
        const q = search.trim().toLowerCase();
        return docs.filter((d) => {
            // Virtual category: docs that need acknowledgment AND haven't been acked yet
            if (filterCategory === "ack_pending") {
                if (!d.requires_acknowledgment || d.acknowledged) return false;
            } else if (filterCategory !== "all" && d.category !== filterCategory) {
                return false;
            }
            if (!q) return true;
            return (
                (d.title || "").toLowerCase().includes(q) ||
                (d.filename || "").toLowerCase().includes(q) ||
                (d.period || "").toLowerCase().includes(q)
            );
        });
    }, [docs, search, filterCategory]);

    // Count of pending-acknowledgment documents — surfaced as a clickable chip
    // next to the "Modifica i tuoi dati" button so the employee instantly sees
    // how many documents still need their formal eIDAS confirmation.
    // Documents with expiration date — surfaced as a friendly banner so the
    // employee can react in time (e.g. renew medical certificate).
    const expiringDocs = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        // Contract-chain rule: an old UNILAV/contract superseded by a later proroga
        // or by the trasformazione a tempo indeterminato is not "expired".
        const CHAIN = ["unilav", "contratto"];
        const cessato = !!meEmployee?.data_cessazione;
        const indeterminato = !!meEmployee && !meEmployee.contratto_scadenza;
        let chainMax = meEmployee?.contratto_scadenza || "";
        for (const d of docs) {
            if (CHAIN.includes(d.category) && d.expires_at && d.expires_at > chainMax) chainMax = d.expires_at;
        }
        const list = [];
        for (const d of docs) {
            if (!d.expires_at) continue;
            if (d.expiry_superseded) continue;
            if (CHAIN.includes(d.category) && (cessato || indeterminato || d.expires_at < chainMax)) continue;
            const exp = new Date(d.expires_at);
            if (isNaN(exp.getTime())) continue;
            const daysLeft = Math.round((exp - today) / 86400000);
            if (daysLeft <= 60) {
                list.push({ ...d, daysLeft });
            }
        }
        list.sort((a, b) => a.daysLeft - b.daysLeft);
        return list;
    }, [docs, meEmployee]);

    const pendingAckDocs = useMemo(
        () => docs.filter((d) => d.requires_acknowledgment && !d.acknowledged),
        [docs],
    );
    const pendingAckCount = pendingAckDocs.length;

    // Most recently received payslip — surfaced as a quick-access card at the top.
    const latestPayslip = useMemo(() => {
        const list = docs.filter((d) => d.category === "busta_paga");
        if (!list.length) return null;
        return list.reduce((a, b) => ((b.uploaded_at || "") > (a.uploaded_at || "") ? b : a));
    }, [docs]);

    // Welcome documents (guide, policies) — highlighted below the summary card.
    const welcomeDocs = useMemo(() => docs.filter((d) => d.is_welcome), [docs]);

    // Admin-chosen featured document (highlighted for all employees).
    const featuredDoc = useMemo(() => docs.find((d) => d.is_featured) || null, [docs]);

    const { sortedItems: filteredSorted, sortKey, sortDir, requestSort } = useSortableTable(filtered, {
        customAccessors: {
            categoria: (d) => d.category || "",
            documento: (d) => (d.filename || d.title || "").toLowerCase(),
            periodo: (d) => d.period_label || d.period_from || "",
        },
    });

    const grouped = useMemo(() => {
        const map = {};
        CATEGORY_ORDER.forEach((c) => (map[c] = []));
        for (const d of docs) {
            (map[d.category] || map.altro).push(d);
        }
        return map;
    }, [docs]);

    // Count "new" docs per category. A doc is "new" if it requires acknowledgment
    // and hasn't been acknowledged yet, or hasn't been downloaded at all.
    const newPerCategory = useMemo(() => {
        const map = {};
        for (const d of docs) {
            const isNew = (d.requires_acknowledgment && !d.acknowledged)
                || (!d.legacy_download && (d.download_count || 0) === 0);
            if (isNew) {
                map[d.category] = (map[d.category] || 0) + 1;
            }
        }
        return map;
    }, [docs]);

    // Auto-expand the first category with new docs on initial load (one-time).
    // If no category has new docs but Acconti has unsigned receipts, auto-open Acconti.
    useEffect(() => {
        if (autoExpandDone || loading) return;
        const firstNewCat = CATEGORY_ORDER.find((c) => (newPerCategory[c] || 0) > 0);
        if (firstNewCat) {
            setFilterCategory(firstNewCat);
            setAutoExpandDone(true);
        } else if (advancesInfo.hasNew) {
            setFilterCategory("acconti");
            setAutoExpandDone(true);
        } else if (docs.length === 0 && advancesInfo.count === 0) {
            // Nothing to show — mark done so we don't re-trigger.
            setAutoExpandDone(true);
        }
    }, [autoExpandDone, loading, newPerCategory, advancesInfo, docs.length]);

    return (
        <div className="min-h-screen bg-[#F4F8FD]" data-testid="employee-portal">
            {/* Header — two-row layout: brand row + actions row */}
            <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    {/* Row 1: Logo + label + user identity */}
                    <div className="flex items-center justify-between gap-4 py-3 sm:py-4">
                        <div className="flex items-center gap-4 sm:gap-6 min-w-0">
                            <img
                                src="/brand/logo-full.jpg"
                                alt={company.ragione_sociale}
                                width={270}
                                height={100}
                                className="h-16 w-auto sm:h-20 lg:h-24"
                                style={{ aspectRatio: "1812 / 674" }}
                                data-testid="employee-header-logo"
                            />
                            <div className="hidden border-l border-slate-200 pl-4 sm:pl-6 sm:block">
                                <div className="text-[11px] font-semibold uppercase tracking-widest text-[#376DB3]/70">Portale HR</div>
                                <div className="font-display text-lg font-bold leading-tight text-[#376DB3] lg:text-xl">Area Personale</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {pendingAckCount > 0 && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setFilterCategory("ack_pending");
                                        setTimeout(() => {
                                            const el = document.querySelector('[data-testid="emp-documents-section"]');
                                            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                        }, 80);
                                    }}
                                    className="group inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 shadow-sm transition-all hover:border-amber-400 hover:bg-amber-100 hover:shadow"
                                    title={`${pendingAckCount} document${pendingAckCount === 1 ? "o" : "i"} ${pendingAckCount === 1 ? "richiede" : "richiedono"} la tua presa visione — clicca per filtrare`}
                                    data-testid="pending-ack-chip"
                                >
                                    <ShieldCheck className="h-3.5 w-3.5" />
                                    <span>
                                        <span className="font-mono-numbers font-bold">{pendingAckCount}</span>{" "}
                                        <span className="hidden md:inline">
                                            {pendingAckCount === 1
                                                ? "documento richiede presa visione"
                                                : "documenti richiedono presa visione"}
                                        </span>
                                        <span className="md:hidden">da firmare</span>
                                    </span>
                                </button>
                            )}
                            {user && (
                                <div className="hidden text-right md:block">
                                    <div className="text-sm font-semibold text-slate-700" data-testid="employee-name">
                                        {user.name}
                                    </div>
                                    <div className="text-[11px] text-slate-500">{user.email}</div>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Row 2: Action buttons (desktop) */}
                    <div className="hidden flex-wrap items-center justify-end gap-2 border-t border-slate-100 py-2 sm:flex">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setGuideFirstTime(false); setGuideOpen(true); }}
                            className="gap-2"
                            title="Guida all'uso del portale"
                            data-testid="employee-guide-btn"
                        >
                            <BookOpen className="h-4 w-4" />
                            <span className="hidden sm:inline">Guida</span>
                        </Button>
                        {!isCessato && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setProfileTab("data"); setProfileOpen(true); }}
                            className="gap-2"
                            title="Modifica i tuoi dati personali o cambia password"
                            data-testid="employee-profile-btn"
                        >
                            <User className="h-4 w-4" />
                            <span className="hidden sm:inline">Modifica i tuoi dati</span>
                        </Button>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setMyUploadsOpen(true)}
                            className="gap-2"
                            title="Documenti che ho inviato all'azienda"
                            data-testid="employee-my-uploads-btn"
                        >
                            <Inbox className="h-4 w-4" />
                            <span className="hidden sm:inline">I miei invii</span>
                        </Button>
                        {canOperate && (presenzeEnabled || richiesteEnabled) && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate("/portale/ore")}
                                className="gap-2 border-[#376DB3] text-[#376DB3] hover:bg-[#376DB3]/5"
                                title="Riepilogo mensile di ore, straordinari e assenze"
                                data-testid="employee-ore-btn"
                            >
                                <Clock4 className="h-4 w-4" />
                                <span className="hidden sm:inline">Le mie ore</span>
                            </Button>
                        )}
                        {canOperate && richiesteEnabled && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate("/portale/richieste")}
                                className="gap-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                                title="Richiedi ferie, permessi o segnala straordinari"
                                data-testid="employee-richieste-btn"
                            >
                                <CalendarDays className="h-4 w-4" />
                                <span className="hidden sm:inline">Ferie e straordinari</span>
                            </Button>
                        )}
                        {canOperate && presenzeEnabled && user?.timbratura_enabled && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate("/timbratura")}
                                className="gap-2 border-[#376DB3] text-[#376DB3] hover:bg-[#376DB3]/5"
                                title="Vai alla timbratura"
                                data-testid="employee-timbratura-btn"
                            >
                                <Clock4 className="h-4 w-4" />
                                <span className="hidden sm:inline">Timbratura</span>
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setUploadOpen(true)}
                            className="gap-2 border-[#376DB3] text-[#376DB3] hover:bg-[#376DB3]/5"
                            title="Invia documento all'azienda"
                            data-testid="employee-upload-btn"
                        >
                            <Upload className="h-4 w-4" />
                            <span className="hidden sm:inline">Invia documento</span>
                        </Button>
                        {user?.role === "operator" && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate("/dipendenti")}
                                className="gap-2 border-[#15498F] font-medium text-[#15498F] hover:bg-[#15498F]/5"
                                title="Torna all'area amministratore"
                                data-testid="back-to-admin-btn"
                            >
                                <ShieldCheck className="h-4 w-4" />
                                <span className="hidden sm:inline">Area amministratore</span>
                            </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={handleLogout} className="gap-2" data-testid="employee-logout">
                            <LogOut className="h-4 w-4" /> Esci
                        </Button>
                    </div>
                    {/* Row 2 mobile: menu con etichette */}
                    <div className="flex items-center justify-end border-t border-slate-100 py-2 sm:hidden">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" className="gap-2" data-testid="employee-mobile-menu-btn">
                                    <Menu className="h-4 w-4" /> Menu
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-60">
                                <DropdownMenuItem onClick={() => { setGuideFirstTime(false); setGuideOpen(true); }} data-testid="mobile-menu-guide">
                                    <BookOpen className="mr-2 h-4 w-4" /> Guida
                                </DropdownMenuItem>
                                {!isCessato && (
                                <DropdownMenuItem onClick={() => { setProfileTab("data"); setProfileOpen(true); }} data-testid="mobile-menu-profile">
                                    <User className="mr-2 h-4 w-4" /> Modifica i tuoi dati
                                </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => setMyUploadsOpen(true)} data-testid="mobile-menu-uploads">
                                    <Inbox className="mr-2 h-4 w-4" /> I miei invii
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setUploadOpen(true)} data-testid="mobile-menu-upload">
                                    <Upload className="mr-2 h-4 w-4" /> Invia documento
                                </DropdownMenuItem>
                                {canOperate && (presenzeEnabled || richiesteEnabled) && (
                                    <DropdownMenuItem onClick={() => navigate("/portale/ore")} data-testid="mobile-menu-ore">
                                        <Clock4 className="mr-2 h-4 w-4" /> Le mie ore
                                    </DropdownMenuItem>
                                )}
                                {canOperate && richiesteEnabled && (
                                    <DropdownMenuItem onClick={() => navigate("/portale/richieste")} data-testid="mobile-menu-richieste">
                                        <CalendarDays className="mr-2 h-4 w-4" /> Ferie e straordinari
                                    </DropdownMenuItem>
                                )}
                                {canOperate && presenzeEnabled && user?.timbratura_enabled && (
                                    <DropdownMenuItem onClick={() => navigate("/timbratura")} data-testid="mobile-menu-timbratura">
                                        <Clock4 className="mr-2 h-4 w-4" /> Timbratura
                                    </DropdownMenuItem>
                                )}
                                {user?.role === "operator" && (
                                    <DropdownMenuItem onClick={() => navigate("/dipendenti")} data-testid="mobile-menu-admin">
                                        <ShieldCheck className="mr-2 h-4 w-4" /> Area amministratore
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600" data-testid="mobile-menu-logout">
                                    <LogOut className="mr-2 h-4 w-4" /> Esci
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Benvenuto</div>
                    <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-[#376DB3] sm:text-4xl">
                        Ciao {user?.name?.split(" ")[1] || user?.name || ""}
                    </h1>
                    <p className="mt-1 text-sm text-slate-600">
                        Qui trovi i tuoi documenti del tuo rapporto di lavoro.
                    </p>
                </div>

                {isCessato && (
                    <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="cessato-readonly-banner">
                        <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                        <div>
                            <b>Rapporto di lavoro cessato il {String(meEmployee.data_cessazione).slice(0, 10).split("-").reverse().join("/")}.</b>{" "}
                            Il portale è in sola consultazione: puoi scaricare e conservare i tuoi documenti (CU, buste paga, contratti, ricevute)
                            {meEmployee.portal_access?.until ? <> fino al <b>{meEmployee.portal_access.until.split("-").reverse().join("/")}</b></> : null}.
                            Per richieste o variazioni contatta l'ufficio personale.
                        </div>
                    </div>
                )}

                {/* Employee summary card (read-only — edits go through the header button) */}
                <EmployeeSummaryCard
                    employeeId={user?.id}
                    refreshKey={summaryRefreshKey}
                />

                {/* La mia settimana: cantieri/giorni/orari (si nasconde se non ci sono assegnazioni) */}
                {canOperate && <MyWeekCard variant="portal" />}

                {/* Featured document (admin-chosen, shown to all employees) */}
                {featuredDoc && (
                    <div
                        className="mt-6 flex flex-wrap items-center gap-3 rounded-lg border border-amber-300/60 bg-gradient-to-r from-amber-50 to-white p-4 shadow-sm"
                        data-testid="featured-doc-card"
                    >
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-amber-100 text-amber-600">
                            <Megaphone className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">
                                In evidenza
                            </div>
                            <div className="break-words font-display text-sm font-bold text-slate-800" data-testid="featured-doc-title">
                                {featuredDoc.title || featuredDoc.filename}
                            </div>
                            <div className="text-[11px] text-slate-500">
                                ricevuto il {formatDateTime(featuredDoc.uploaded_at)}
                            </div>
                        </div>
                        {featuredDoc.requires_acknowledgment && !featuredDoc.acknowledged && (
                            <Badge
                                variant="outline"
                                className="gap-1 border-amber-300 bg-amber-50 text-amber-700"
                                data-testid="featured-doc-ack-badge"
                            >
                                <ShieldCheck className="h-3 w-3" />
                                Presa visione richiesta
                            </Badge>
                        )}
                        <div className="flex w-full items-center gap-2 sm:w-auto sm:flex-shrink-0">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePreview(featuredDoc)}
                                className="gap-1.5"
                                data-testid="featured-doc-preview-btn"
                            >
                                <Eye className="h-3.5 w-3.5" />
                                Anteprima
                            </Button>
                            <Button
                                size="sm"
                                onClick={() => handleDownload(featuredDoc)}
                                disabled={downloadingId === featuredDoc.id}
                                className="gap-1.5 bg-amber-600 text-white hover:bg-amber-700"
                                data-testid="featured-doc-download-btn"
                            >
                                {downloadingId === featuredDoc.id ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <Download className="h-3.5 w-3.5" />
                                )}
                                Scarica
                            </Button>
                        </div>
                    </div>
                )}

                {/* Welcome documents — highlighted below the summary card */}
                {welcomeDocs.length > 0 && (
                    <div
                        className="mt-6 rounded-lg border border-[#376DB3]/25 bg-gradient-to-r from-[#5BAEDB]/10 to-white p-4 shadow-sm"
                        data-testid="welcome-docs-card"
                    >
                        <div className="mb-2 flex items-center gap-3">
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                                <Gift className="h-5 w-5" />
                            </div>
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-[#376DB3]">
                                    Benvenuto
                                </div>
                                <div className="text-[11px] text-slate-500">
                                    Guide e informazioni utili messe a disposizione dall'azienda
                                </div>
                            </div>
                        </div>
                        <ul className="space-y-1.5">
                            {welcomeDocs.map((d) => (
                                <li
                                    key={d.id}
                                    className="rounded-md border border-[#376DB3]/15 bg-white px-3 py-2"
                                    data-testid={`welcome-doc-item-${d.id}`}
                                >
                                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                        <div className="flex min-w-0 flex-1 items-center gap-2">
                                            <BookOpen className="h-4 w-4 flex-shrink-0 text-[#376DB3]" />
                                            <span className="min-w-0 break-words text-sm font-medium text-slate-800">
                                                {d.title || d.filename}
                                            </span>
                                        </div>
                                        <div className="flex flex-shrink-0 items-center gap-2 pl-6 sm:pl-0">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handlePreview(d)}
                                            className="h-7 gap-1.5 px-2.5 text-xs"
                                            data-testid={`welcome-doc-preview-${d.id}`}
                                        >
                                            <Eye className="h-3.5 w-3.5" />
                                            Anteprima
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={() => handleDownload(d)}
                                            disabled={downloadingId === d.id}
                                            className="h-7 gap-1.5 bg-[#376DB3] px-2.5 text-xs text-white hover:bg-[#376DB3]/90"
                                            data-testid={`welcome-doc-download-${d.id}`}
                                        >
                                            {downloadingId === d.id ? (
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            ) : (
                                                <Download className="h-3.5 w-3.5" />
                                            )}
                                            Scarica
                                        </Button>
                                    </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Latest payslip quick-access card */}
                {latestPayslip && (
                    <div
                        className="mt-6 flex flex-wrap items-center gap-3 rounded-lg border border-[#376DB3]/25 bg-gradient-to-r from-[#5BAEDB]/10 to-white p-4 shadow-sm"
                        data-testid="latest-payslip-card"
                    >
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                            <Receipt className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-[#376DB3]">
                                Ultima busta paga
                            </div>
                            <div className="break-words font-display text-sm font-bold text-slate-800" data-testid="latest-payslip-title">
                                {latestPayslip.title || latestPayslip.filename}
                                {payslipPeriodLabel(latestPayslip.period) && (
                                    <span className="ml-2 font-normal text-slate-500">
                                        ({payslipPeriodLabel(latestPayslip.period)})
                                    </span>
                                )}
                            </div>
                            <div className="text-[11px] text-slate-500">
                                ricevuta il {formatDateTime(latestPayslip.uploaded_at)}
                            </div>
                        </div>
                        {latestPayslip.requires_acknowledgment && !latestPayslip.acknowledged && (
                            <Badge
                                variant="outline"
                                className="gap-1 border-amber-300 bg-amber-50 text-amber-700"
                                data-testid="latest-payslip-ack-badge"
                            >
                                <ShieldCheck className="h-3 w-3" />
                                Presa visione richiesta
                            </Badge>
                        )}
                        <div className="flex w-full items-center gap-2 sm:w-auto sm:flex-shrink-0">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePreview(latestPayslip)}
                                className="gap-1.5"
                                data-testid="latest-payslip-preview-btn"
                            >
                                <Eye className="h-3.5 w-3.5" />
                                Anteprima
                            </Button>
                            <Button
                                size="sm"
                                onClick={() => handleDownload(latestPayslip)}
                                disabled={downloadingId === latestPayslip.id}
                                className="gap-1.5 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                                data-testid="latest-payslip-download-btn"
                            >
                                {downloadingId === latestPayslip.id ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <Download className="h-3.5 w-3.5" />
                                )}
                                Scarica
                            </Button>
                        </div>
                    </div>
                )}

                {/* Pending signatures banner — first thing the employee sees when
                    documents are waiting for their formal acknowledgment */}
                {pendingAckCount > 0 && (
                    <div
                        className="mb-6 mt-6 rounded-lg border border-[#376DB3]/30 bg-gradient-to-br from-[#376DB3]/10 to-white p-4 shadow-sm"
                        data-testid="employee-pending-ack-banner"
                    >
                        <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-[#376DB3]/15 text-[#376DB3]">
                                <FileSignature className="h-5 w-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="font-display text-sm font-bold text-[#15498F]" data-testid="pending-ack-banner-title">
                                    {pendingAckCount === 1
                                        ? "Hai 1 documento da firmare"
                                        : `Hai ${pendingAckCount} documenti da firmare`}
                                </div>
                                <p className="mt-0.5 text-xs text-slate-600">
                                    {pendingAckCount === 1
                                        ? "È richiesta la tua presa visione. Clicca sul documento per confermarla subito."
                                        : "È richiesta la tua presa visione. Clicca su un documento per confermarla subito."}
                                </p>
                                <div className="mt-3 space-y-1.5">
                                    {pendingAckDocs.slice(0, 4).map((d) => (
                                        <button
                                            key={d.id}
                                            type="button"
                                            onClick={() => setAckDoc(d)}
                                            className="flex w-full items-center justify-between gap-2 rounded-md border border-[#376DB3]/20 bg-white px-3 py-2 text-left text-sm transition-colors hover:bg-[#376DB3]/5"
                                            data-testid={`pending-ack-banner-doc-${d.id}`}
                                        >
                                            <span className="min-w-0 flex-1">
                                                <span className="mr-2 rounded-full bg-[#376DB3]/10 px-2 py-0.5 text-[10px] font-semibold text-[#376DB3]">
                                                    {d.category_label}
                                                </span>
                                                <span className="truncate text-slate-700">{d.title || d.filename}</span>
                                            </span>
                                            <span className="flex-shrink-0 text-[11px] font-semibold text-[#376DB3]">Firma →</span>
                                        </button>
                                    ))}
                                </div>
                                {pendingAckCount > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => setBulkAckOpen(true)}
                                        className="mt-3 inline-flex items-center gap-2 rounded-md bg-[#376DB3] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#376DB3]/90"
                                        data-testid="bulk-ack-open-btn"
                                    >
                                        <FileSignature className="h-3.5 w-3.5" />
                                        Firma tutti ({pendingAckCount})
                                    </button>
                                )}
                                {pendingAckCount > 4 && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setFilterCategory("ack_pending");
                                            setTimeout(() => {
                                                const el = document.querySelector('[data-testid="emp-documents-section"]');
                                                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                            }, 80);
                                        }}
                                        className="mt-2 text-xs font-semibold text-[#376DB3] hover:underline"
                                        data-testid="pending-ack-banner-view-all"
                                    >
                                        Vedi tutti i {pendingAckCount} documenti da firmare →
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Documenti richiesti dall'ufficio */}
                {docRequests.length > 0 && (
                    <div
                        className="mb-6 mt-6 rounded-lg border border-[#376DB3]/40 bg-gradient-to-br from-sky-50 to-white p-4 shadow-sm"
                        data-testid="doc-requests-banner"
                    >
                        <div className="flex items-start gap-3">
                            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                                <FileText className="h-5 w-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="font-display text-base font-bold text-slate-800">
                                    {docRequests.length === 1
                                        ? "L'ufficio ti ha richiesto un documento"
                                        : `L'ufficio ti ha richiesto ${docRequests.length} documenti`}
                                </div>
                                <ul className="mt-3 space-y-2">
                                    {docRequests.map((r) => (
                                        <li
                                            key={r.id}
                                            className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-white px-3 py-2 shadow-sm ring-1 ring-slate-100"
                                            data-testid={`doc-request-item-${r.id}`}
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-semibold text-slate-800">{r.doc_type}</div>
                                                {r.message && (
                                                    <div className="mt-0.5 text-xs text-slate-500">{r.message}</div>
                                                )}
                                            </div>
                                            <Button
                                                size="sm"
                                                onClick={() => {
                                                    setUploadPrefill(r.doc_type);
                                                    setUploadRequestId(r.id);
                                                    setUploadOpen(true);
                                                }}
                                                className="h-8 flex-shrink-0 gap-1.5 bg-[#376DB3] px-3 text-xs text-white hover:bg-[#376DB3]/90"
                                                data-testid={`doc-request-upload-btn-${r.id}`}
                                            >
                                                <Upload className="h-3.5 w-3.5" />
                                                Carica documento
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {/* Expiring documents banner — alerts the employee about docs that need attention
                    (medical certificates, identity documents, contracts, etc.) within 60 days */}
                {expiringDocs.length > 0 && (
                    <div
                        className="mb-6 mt-6 rounded-lg border border-amber-300 bg-gradient-to-br from-amber-50 to-white p-4 shadow-sm"
                        data-testid="employee-expiring-banner"
                    >
                        <div className="flex items-start gap-3">
                            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-amber-100 text-amber-700">
                                <AlertCircle className="h-5 w-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-display text-base font-bold text-amber-900">
                                    {expiringDocs.some((d) => d.daysLeft < 0)
                                        ? "Attenzione: hai documenti scaduti o in scadenza"
                                        : "Documenti in scadenza"}
                                </div>
                                <p className="text-xs text-amber-800">
                                    {expiringDocs.length === 1
                                        ? "1 documento ha bisogno della tua attenzione."
                                        : `${expiringDocs.length} documenti hanno bisogno della tua attenzione.`}
                                    {" "}Se necessario, invia il nuovo documento usando il pulsante <b>"Invia documento"</b> in alto.
                                </p>
                                <ul className="mt-3 space-y-1.5">
                                    {expiringDocs.slice(0, 4).map((d) => {
                                        const expired = d.daysLeft < 0;
                                        return (
                                            <li
                                                key={d.id}
                                                className={`flex items-center justify-between gap-2 rounded px-2 py-1.5 text-sm ${
                                                    expired
                                                        ? "bg-red-50 text-red-800"
                                                        : d.daysLeft <= 30
                                                          ? "bg-amber-100/70 text-amber-900"
                                                          : "bg-white text-slate-700"
                                                }`}
                                                data-testid={`employee-expiring-doc-${d.id}`}
                                            >
                                                <div className="min-w-0 flex-1">
                                                    <div className="truncate font-medium" title={d.filename}>
                                                        {d.filename}
                                                    </div>
                                                    <div className="text-[11px] opacity-80">
                                                        {CATEGORY_LABELS[d.category] || d.category} · scadenza {d.expires_at.split("-").reverse().join("/")}
                                                    </div>
                                                </div>
                                                <div className="flex-shrink-0 text-xs font-semibold">
                                                    {expired
                                                        ? `scaduto da ${Math.abs(d.daysLeft)} gg`
                                                        : d.daysLeft === 0
                                                          ? "scade oggi"
                                                          : d.daysLeft === 1
                                                            ? "scade domani"
                                                            : `tra ${d.daysLeft} gg`}
                                                </div>
                                                <Button
                                                    size="sm"
                                                    onClick={() => {
                                                        setUploadPrefill(`Rinnovo: ${CATEGORY_LABELS[d.category] || "documento"} — ${d.filename}`);
                                                        setUploadOpen(true);
                                                    }}
                                                    className="h-7 flex-shrink-0 gap-1.5 bg-[#376DB3] px-2.5 text-[11px] text-white hover:bg-[#376DB3]/90"
                                                    data-testid={`expiring-upload-btn-${d.id}`}
                                                >
                                                    <Upload className="h-3 w-3" />
                                                    Invia nuovo documento
                                                </Button>
                                            </li>
                                        );
                                    })}
                                </ul>
                                {expiringDocs.length > 4 && (
                                    <div className="mt-2 text-[11px] text-amber-700">
                                        + altri {expiringDocs.length - 4} documenti in scadenza
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Category chips + Acconti (Acconti shown only if has receipts).
                    Compact pills that wrap naturally — no clipped labels. */}
                <div className="mb-8 flex flex-wrap gap-2" data-testid="category-cards-section">
                    {CATEGORY_ORDER.map((cat) => {
                        const Icon = CATEGORY_ICONS[cat] || File;
                        const count = grouped[cat]?.length || 0;
                        const newCount = newPerCategory[cat] || 0;
                        const active = filterCategory === cat;
                        return (
                            <button
                                key={cat}
                                type="button"
                                onClick={() => setFilterCategory(active ? "" : cat)}
                                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 transition-colors ${
                                    active
                                        ? "border-[#376DB3] bg-[#376DB3] text-white shadow"
                                        : "border-slate-200 bg-white text-slate-700 hover:border-[#376DB3]/50 hover:bg-[#5BAEDB]/10"
                                }`}
                                data-testid={`category-card-${cat}`}
                            >
                                <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${active ? "text-white" : "text-[#376DB3]"}`} />
                                <span className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide">
                                    {CATEGORY_LABELS[cat]}
                                </span>
                                <span className={`font-display text-sm font-bold font-mono-numbers ${active ? "text-white" : "text-[#376DB3]"}`}>
                                    {count}
                                </span>
                                {newCount > 0 && (
                                    <span
                                        className="flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white"
                                        title={`${newCount} ${newCount === 1 ? "nuovo documento" : "nuovi documenti"}`}
                                    >
                                        {newCount}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                    {/* Acconti widget — always visible so anyone can request an advance */}
                    <button
                        type="button"
                        onClick={() => setFilterCategory(filterCategory === "acconti" ? "" : "acconti")}
                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 transition-colors ${
                            filterCategory === "acconti"
                                ? "border-emerald-600 bg-emerald-600 text-white shadow"
                                : "border-slate-200 bg-white text-slate-700 hover:border-emerald-500/50 hover:bg-emerald-50"
                        }`}
                        data-testid="category-card-acconti"
                    >
                        <Wallet className={`h-3.5 w-3.5 flex-shrink-0 ${filterCategory === "acconti" ? "text-white" : "text-emerald-600"}`} />
                        <span className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide">
                            Acconti
                        </span>
                        {advancesInfo.count > 0 && (
                            <span className={`font-display text-sm font-bold font-mono-numbers ${filterCategory === "acconti" ? "text-white" : "text-emerald-700"}`}>
                                {advancesInfo.count}
                            </span>
                        )}
                        {advancesInfo.hasNew && (
                            <span
                                className="flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-white"
                                title="Hai ricevute da firmare"
                            >
                                <AlertCircle className="h-3 w-3" />
                            </span>
                        )}
                    </button>
                </div>

                {/* Acconti widget — always mounted to keep count loaded; list visible only when selected */}
                <div className={filterCategory === "acconti" ? "mb-6" : "hidden"}>
                    <EmployeeAdvancesCard
                        onStatsChange={setAdvancesInfo}
                        defaultOpen
                        canRequest={!isCessato}
                    />
                </div>

                {/* Filters — visible only when a category is selected and not Acconti */}
                {filterCategory && filterCategory !== "acconti" && (
                <>
                <div data-testid="emp-documents-section" />
                {filterCategory === "ack_pending" && (
                    <div className="mb-3 flex items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        <div className="flex items-center gap-2">
                            <ShieldCheck className="h-4 w-4 text-amber-600" />
                            <span>
                                Stai visualizzando solo i documenti che richiedono la tua <b>presa visione</b>.
                                Scarica ogni file e conferma la presa visione nel dialog.
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={() => setFilterCategory("all")}
                            className="rounded-md border border-amber-400 bg-white px-2 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100"
                            data-testid="clear-ack-filter"
                        >
                            Mostra tutti
                        </button>
                    </div>
                )}
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="relative flex-1 max-w-md">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <Input
                            placeholder="Cerca per titolo, file, periodo…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9"
                            data-testid="employee-search"
                        />
                    </div>
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                        <SelectTrigger className="w-full sm:w-56" data-testid="employee-category-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tutte le categorie</SelectItem>
                            {CATEGORY_ORDER.map((c) => (
                                <SelectItem key={c} value={c}>
                                    {CATEGORY_LABELS[c]}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Documents table */}
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50 hover:bg-slate-50">
                                <SortableHead sortKey="categoria" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="hidden sm:table-cell">
                                    Categoria
                                </SortableHead>
                                <SortableHead sortKey="documento" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort}>
                                    Documento
                                </SortableHead>
                                <SortableHead sortKey="periodo" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="hidden md:table-cell">
                                    Periodo
                                </SortableHead>
                                <SortableHead sortKey="uploaded_at" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="hidden lg:table-cell">
                                    Caricato
                                </SortableHead>
                                <SortableHead sortKey="size_bytes" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="hidden sm:table-cell">
                                    Dimensione
                                </SortableHead>
                                <TableHead className="text-right">Azione</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="py-16 text-center">
                                        <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" />
                                    </TableCell>
                                </TableRow>
                            ) : filteredSorted.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="py-16 text-center">
                                        <FileText className="mx-auto mb-3 h-10 w-10 text-slate-300" />
                                        <div className="font-display text-base font-semibold text-slate-700">
                                            {search || filterCategory !== "all" ? "Nessun risultato" : "Nessun documento"}
                                        </div>
                                        <p className="mt-1 text-sm text-slate-500">
                                            {search || filterCategory !== "all"
                                                ? "Prova a modificare i filtri."
                                                : "I documenti caricati dall'amministrazione appariranno qui."}
                                        </p>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredSorted.map((doc) => (
                                    <TableRow key={doc.id} data-testid={`doc-row-${doc.id}`}>
                                        <TableCell className="hidden sm:table-cell">
                                            <Badge variant="secondary">{doc.category_label}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="secondary" className="mb-1 sm:hidden">{doc.category_label}</Badge>
                                            <div className="flex flex-wrap items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handlePreview(doc)}
                                                    className="min-w-0 break-words text-left font-medium text-[#376DB3] hover:underline"
                                                    title="Anteprima documento"
                                                    data-testid={`preview-name-${doc.id}`}
                                                >
                                                    {doc.title || doc.filename}
                                                </button>
                                                <DownloadStatusBadge
                                                    downloadCount={doc.download_count}
                                                    lastDownloadedAt={doc.last_downloaded_at}
                                                    acknowledged={doc.acknowledged}
                                                    legacyDownload={doc.legacy_download}
                                                    testId={`emp-download-status-${doc.id}`}
                                                />
                                                <BadgeHelpButton
                                                    sectionId="download"
                                                    onOpen={openGuideAt}
                                                    label="Cosa significa questo stato?"
                                                    testid={`help-download-${doc.id}`}
                                                />
                                                {/*
                                                  Mostra "Presa visione richiesta" solo se il documento
                                                  richiede ack E il dipendente non l'ha ancora fatto.
                                                  Una volta scaricato/ratificato (legacy o nativo) lo stato
                                                  è già comunicato dal DownloadStatusBadge accanto ("Visto"
                                                  o "Visto (legacy)") quindi il badge ambra è ridondante.
                                                */}
                                                {doc.requires_acknowledgment && !doc.acknowledged && (
                                                    <>
                                                        <Badge
                                                            variant="outline"
                                                            className="gap-1 border-amber-300 bg-amber-50 text-amber-700"
                                                            data-testid={`ack-badge-${doc.id}`}
                                                            title="Documento con valore legale (eIDAS) — richiede la tua presa visione. Scarica il file per registrare la consegna."
                                                        >
                                                            <ShieldCheck className="h-3 w-3" />
                                                            Presa visione richiesta
                                                        </Badge>
                                                        <BadgeHelpButton
                                                            sectionId="ack"
                                                            onOpen={openGuideAt}
                                                            label="Cosa vuol dire 'presa visione'?"
                                                            testid={`help-ack-${doc.id}`}
                                                        />
                                                    </>
                                                )}
                                            </div>
                                            {doc.title && (
                                                <button
                                                    type="button"
                                                    onClick={() => handlePreview(doc)}
                                                    className="text-left text-xs text-slate-500 hover:text-[#376DB3] hover:underline"
                                                    title="Anteprima documento"
                                                >
                                                    {doc.filename}
                                                </button>
                                            )}
                                        </TableCell>
                                        <TableCell className="hidden md:table-cell font-mono-numbers text-slate-700">
                                            {doc.period || "—"}
                                        </TableCell>
                                        <TableCell className="hidden lg:table-cell text-slate-600">
                                            {formatDateTime(doc.uploaded_at)}
                                        </TableCell>
                                        <TableCell className="hidden sm:table-cell text-slate-600 font-mono-numbers">
                                            {formatBytes(doc.size_bytes)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                size="sm"
                                                onClick={() => handleDownload(doc)}
                                                disabled={downloadingId === doc.id}
                                                className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                                                data-testid={`download-${doc.id}`}
                                            >
                                                {downloadingId === doc.id ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <Download className="h-4 w-4" />
                                                )}
                                                <span className="hidden sm:inline">Scarica</span>
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
                </>
                )}

                {/* Empty state when no category is selected */}
                {!filterCategory && !loading && (
                    <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center">
                        <FileText className="mx-auto mb-3 h-10 w-10 text-slate-300" />
                        <div className="font-display text-base font-semibold text-slate-700">
                            Seleziona una categoria
                        </div>
                        <p className="mt-1 text-sm text-slate-500">
                            Tocca una delle card sopra per visualizzare i documenti.
                        </p>
                    </div>
                )}

                <div className="mt-8 border-t border-slate-200 pt-6 text-center text-xs text-slate-500">
                    {company.ragione_sociale} · {company.sede}
                    <div className="mt-1">
                        Per assistenza e comunicazioni:{" "}
                        <a
                            href="mailto:dipendenti@splendorsud.com"
                            className="font-medium text-[#376DB3] hover:underline"
                            data-testid="portal-support-email"
                        >
                            dipendenti@splendorsud.com
                        </a>
                    </div>
                    <div className="mt-1">
                        Tel.{" "}
                        <a href="tel:+39099375014" className="font-medium text-[#376DB3] hover:underline" data-testid="portal-support-tel">
                            099.375014
                        </a>{" "}
                        · Cell.{" "}
                        <a href="tel:+393481966463" className="font-medium text-[#376DB3] hover:underline" data-testid="portal-support-cell">
                            348.1966463
                        </a>
                    </div>
                </div>
            </main>

            <ConsentInformativaDialog />
            <BulkAcknowledgmentDialog
                docs={pendingAckDocs}
                open={bulkAckOpen}
                onClose={() => setBulkAckOpen(false)}
                onConfirm={async () => {
                    try {
                        const { data: res } = await api.post("/documents/ack-bulk", {
                            document_ids: pendingAckDocs.map((d) => d.id),
                        });
                        const failed = (res.failed || []).length;
                        if (res.acknowledged > 0) {
                            toast.success(`Presa visione registrata per ${res.acknowledged} document${res.acknowledged === 1 ? "o" : "i"}`);
                        }
                        if (failed > 0) {
                            toast.error(`${failed} documenti non confermati: riprova più tardi`);
                        }
                        await refreshDocs();
                    } catch (e) {
                        const detail = e.response?.data?.detail;
                        toast.error((typeof detail === "object" ? detail?.message : detail) || "Errore nella conferma di presa visione");
                    }
                }}
            />
            <AcknowledgmentDialog
                doc={ackDoc}
                open={!!ackDoc}
                onClose={() => setAckDoc(null)}
                onConfirm={async () => { if (ackDoc) await performDownload(ackDoc, true); }}
            />
            <AcknowledgmentDialog
                doc={pendingPreviewAckDoc}
                open={!!pendingPreviewAckDoc}
                onClose={() => setPendingPreviewAckDoc(null)}
                onConfirm={async () => {
                    if (pendingPreviewAckDoc) {
                        setPreviewDoc({ doc: pendingPreviewAckDoc, acknowledged: true });
                    }
                }}
            />
            <DocumentPreviewDialog
                open={!!previewDoc}
                document={previewDoc?.doc}
                acknowledged={previewDoc?.acknowledged || false}
                onClose={() => { setPreviewDoc(null); refreshDocs(); }}
            />
            <EmployeeProfileDialog
                open={profileOpen}
                onClose={() => setProfileOpen(false)}
                defaultTab={profileTab}
                employee={meEmployee}
                onEmployeeUpdated={() => setSummaryRefreshKey((k) => k + 1)}
                onOpenUpload={() => setUploadOpen(true)}
            />
            <MyUploadsDialog open={myUploadsOpen} onClose={() => setMyUploadsOpen(false)} />
            <EmployeeUploadDialog
                open={uploadOpen}
                onClose={() => { setUploadOpen(false); setUploadPrefill(""); setUploadRequestId(null); }}
                prefillTitle={uploadPrefill}
                requestId={uploadRequestId}
            />
            <EmployeeGuideDialog
                open={guideOpen}
                onClose={() => { setGuideOpen(false); setGuideFocusSection(null); }}
                userId={user?.id}
                isFirstTime={guideFirstTime}
                focusSectionId={guideFocusSection}
            />
            {/* Mini-onboarding: 3-step tour shown only after the welcome guide has been seen */}
            <OnboardingTour
                storageKey={user?.id ? `ss_onboarding_done_v1_${user.id}` : "ss_onboarding_done_v1"}
                autoStart={!!user?.id && !loading && hasSeenGuide(user.id) && !guideOpen}
                delayMs={500}
                steps={[
                    {
                        target: '[data-testid="category-cards-section"]',
                        title: "I tuoi documenti, raggruppati",
                        placement: "bottom",
                        body: (
                            <>
                                Qui trovi tutti i documenti del tuo rapporto di lavoro,{" "}
                                <strong>divisi per categoria</strong> (buste paga, CU, contratti, ecc.).
                                Clicca una card per filtrare solo quella tipologia.
                                Il pallino rosso segnala documenti nuovi non ancora visti.
                            </>
                        ),
                    },
                    {
                        target: '[data-testid="pending-ack-chip"]',
                        title: "Documenti che richiedono presa visione",
                        placement: "bottom",
                        body: (
                            <>
                                Questo badge ti avvisa quando hai documenti che{" "}
                                <strong>richiedono la tua firma di presa visione</strong>{" "}
                                (es. comunicazioni ufficiali ai sensi del D.Lgs. 152/1997).
                                Cliccalo per filtrare e firmarli rapidamente.
                            </>
                        ),
                    },
                    {
                        target: '[data-testid="employee-upload-btn"]',
                        title: "Invia un documento all'ufficio HR",
                        placement: "bottom",
                        body: (
                            <>
                                Hai un certificato medico, una richiesta ferie o un altro documento da consegnare?
                                Usa <strong>"Invia documento"</strong> per caricarlo in sicurezza:
                                arriverà direttamente all'ufficio del personale.
                            </>
                        ),
                    },
                ]}
            />
        </div>
    );
}


function EmployeeProfileDialog({ open, onClose, defaultTab = "data", employee, onEmployeeUpdated, onOpenUpload }) {
    const [tab, setTab] = useState(defaultTab);
    const [oldPw, setOldPw] = useState("");
    const [newPw, setNewPw] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [pwError, setPwError] = useState("");

    useEffect(() => {
        if (!open) {
            setOldPw(""); setNewPw(""); setConfirmPw(""); setPwError("");
        } else {
            setTab(defaultTab);
        }
    }, [open, defaultTab]);

    const handleChangePw = async (e) => {
        e.preventDefault();
        setPwError("");
        if (newPw.length < 6) { setPwError("La password deve avere almeno 6 caratteri"); return; }
        if (newPw !== confirmPw) { setPwError("Le password non coincidono"); return; }
        setSubmitting(true);
        try {
            await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
            toast.success("Password aggiornata correttamente");
            onClose();
        } catch (err) {
            const detail = err.response?.data?.detail || "Errore aggiornamento";
            setPwError(detail);
            toast.error(detail);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="profile-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <User className="h-5 w-5 text-[#376DB3]" />
                        Modifica i tuoi dati
                    </DialogTitle>
                </DialogHeader>

                <div className="flex gap-1 rounded-md bg-slate-100 p-1">
                    <button
                        type="button"
                        onClick={() => setTab("data")}
                        className={`flex-1 rounded px-3 py-1.5 text-sm font-medium ${tab === "data" ? "bg-white text-[#376DB3] shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                        data-testid="profile-tab-data"
                    >
                        I miei dati
                    </button>
                    <button
                        type="button"
                        onClick={() => setTab("password")}
                        className={`flex-1 rounded px-3 py-1.5 text-sm font-medium ${tab === "password" ? "bg-white text-[#376DB3] shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                        data-testid="profile-tab-password"
                    >
                        Cambia password
                    </button>
                </div>

                {tab === "data" ? (
                    <EditContactDataForm
                        employee={employee}
                        onClose={onClose}
                        onSaved={onEmployeeUpdated}
                        onOpenUpload={() => { onClose(); onOpenUpload?.(); }}
                    />
                ) : (
                    <form onSubmit={handleChangePw} className="space-y-3" data-testid="change-pw-form">
                        <div>
                            <Label htmlFor="old-pw">Password attuale</Label>
                            <Input
                                id="old-pw"
                                type="password"
                                value={oldPw}
                                onChange={(e) => setOldPw(e.target.value)}
                                required
                                autoComplete="current-password"
                                className="mt-1"
                                data-testid="old-pw-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="new-pw">Nuova password</Label>
                            <Input
                                id="new-pw"
                                type="password"
                                value={newPw}
                                onChange={(e) => setNewPw(e.target.value)}
                                required
                                autoComplete="new-password"
                                className="mt-1"
                                data-testid="new-pw-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="confirm-pw">Conferma nuova password</Label>
                            <Input
                                id="confirm-pw"
                                type="password"
                                value={confirmPw}
                                onChange={(e) => setConfirmPw(e.target.value)}
                                required
                                autoComplete="new-password"
                                className="mt-1"
                                data-testid="confirm-pw-input"
                            />
                        </div>
                        {pwError && (
                            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="change-pw-error">
                                <span>{pwError}</span>
                            </div>
                        )}
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
                            <Button type="submit" disabled={submitting} className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90" data-testid="change-pw-submit">
                                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                <KeyRound className="h-4 w-4" /> Aggiorna password
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}


function EmployeeUploadDialog({ open, onClose, prefillTitle, requestId }) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) { setTitle(""); setDescription(""); setFile(null); }
        else if (prefillTitle) { setTitle(prefillTitle); }
    }, [open, prefillTitle]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) { toast.error("Seleziona un file"); return; }
        if (title.trim().length < 2) { toast.error("Titolo obbligatorio"); return; }
        setSubmitting(true);
        const fd = new FormData();
        fd.append("title", title.trim());
        if (description.trim()) fd.append("description", description.trim());
        if (requestId) fd.append("request_id", requestId);
        fd.append("file", file);
        try {
            await api.post("/employee-uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Documento inviato all'azienda");
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore invio");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="upload-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <Upload className="h-5 w-5 text-[#376DB3]" />
                        Invia documento all'azienda
                    </DialogTitle>
                    <DialogDescription>
                        Carica un documento che vuoi inviare all'ufficio HR (es. richiesta ferie, certificato medico, comunicazione personale).
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-3" data-testid="upload-form">
                    <div>
                        <Label htmlFor="up-title">Titolo *</Label>
                        <Input
                            id="up-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="es. Richiesta ferie agosto 2026"
                            required
                            autoFocus
                            className="mt-1"
                            data-testid="upload-title-input"
                        />
                    </div>
                    <div>
                        <Label htmlFor="up-desc">Descrizione (opzionale)</Label>
                        <textarea
                            id="up-desc"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-[#376DB3] focus:outline-none focus:ring-1 focus:ring-[#376DB3]"
                            placeholder="Aggiungi un breve messaggio per HR…"
                            data-testid="upload-desc-input"
                        />
                    </div>
                    <div>
                        <Label htmlFor="up-file">File *</Label>
                        <Input
                            id="up-file"
                            type="file"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            required
                            className="mt-1"
                            data-testid="upload-file-input"
                        />
                        {file && (
                            <div className="mt-1 text-xs text-slate-500">
                                {file.name} · {(file.size / 1024).toFixed(1)} KB
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
                        <Button type="submit" disabled={submitting || !file} className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90" data-testid="upload-submit-btn">
                            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                            <Upload className="h-4 w-4" /> Invia
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
